import React, { useEffect, useState } from "react";
import {
  adminGetSeasonPassConfig, adminPutSeasonPassConfig, adminResetSeasonPass,
} from "../../lib/api";

const RULES = [
  ["match_played", "Match played"],
  ["match_won", "Match won"],
  ["chat_message", "Chat message"],
  ["chat_daily_cap", "Chat XP daily cap"],
  ["dispute_action", "Open dispute"],
  ["onboarding_done", "Onboarding done"],
  ["daily_login", "Daily login"],
];

/**
 * SeasonTierEditor — admin-only XP rule + tier reward configurator.
 * Lives under the SeasonPassSection but is its own file so individual
 * tier rows + XP rule inputs can evolve without ballooning the parent.
 */
export function SeasonTierEditor({ onToast }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminGetSeasonPassConfig().then(setCfg).catch((e) => {
      onToast?.(e?.response?.data?.detail || "Couldn't load tier config.");
    });
  }, []); // eslint-disable-line

  if (!cfg) return null;

  const setRule = (key, v) => {
    const n = Math.max(0, parseInt(v) || 0);
    setCfg((c) => ({ ...c, xp_rules: { ...c.xp_rules, [key]: n } }));
  };

  const setTier = (i, patch) => {
    setCfg((c) => {
      const next = [...c.tiers];
      next[i] = { ...next[i], ...patch };
      return { ...c, tiers: next };
    });
  };

  const addTier = () => {
    setCfg((c) => {
      const last = c.tiers[c.tiers.length - 1];
      const newTier = last ? last.tier + 1 : 1;
      const newXp = last ? last.xp_required + 250 : 100;
      return {
        ...c,
        tiers: [...c.tiers, {
          tier: newTier, xp_required: newXp,
          free_reward: "✨ New reward", pro_reward: "💫 New PRO reward",
          is_milestone: newTier % 5 === 0,
        }],
      };
    });
  };

  const removeTier = (i) => {
    if (!window.confirm("Remove this tier?")) return;
    setCfg((c) => ({ ...c, tiers: c.tiers.filter((_, idx) => idx !== i) }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const fresh = await adminPutSeasonPassConfig(cfg);
      setCfg(fresh);
      onToast?.("Tier config saved.");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(false); }
  };

  const resetAll = async () => {
    if (!window.confirm("WIPE every user's XP + claims? This cannot be undone.")) return;
    setBusy(true);
    try {
      const r = await adminResetSeasonPass();
      onToast?.(`Reset complete — ${r.reset} users wiped.`);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't reset.");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="admin-season-tier-editor" className="rounded-2xl border border-white/10 bg-black/40 p-5 space-y-5 mt-6">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
            <i className="fas fa-sliders mr-1" />XP RULES + TIER REWARDS
          </p>
          <p className="text-[11px] text-white/45">
            Edit how XP is earned and what each tier unlocks. Changes are live the moment you save.
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={save} disabled={busy}
                  data-testid="admin-tier-save-btn"
                  className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon disabled:opacity-50">
            {busy ? "…" : <><i className="fas fa-floppy-disk mr-1" />SAVE CONFIG</>}
          </button>
          <button type="button" onClick={resetAll} disabled={busy}
                  data-testid="admin-tier-reset-btn"
                  className="text-[11px] tracking-widest px-4 py-2 rounded-xl bg-[#ff3a3a]/15 border border-[#ff3a3a]/40 text-[#14b8a6] hover:bg-[#ff3a3a]/25 disabled:opacity-50">
            <i className="fas fa-trash mr-1" />WIPE ALL XP
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {RULES.map(([key, label]) => (
          <label key={key} className="block">
            <span className="text-[10px] tracking-widest text-[#a3a3ac]">{label.toUpperCase()}</span>
            <input
              type="number" min="0" max="10000"
              value={cfg.xp_rules[key] ?? 0}
              onChange={(e) => setRule(key, e.target.value)}
              data-testid={`admin-xprule-${key}`}
              className="mt-1 w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
            />
          </label>
        ))}
      </div>

      <div className="space-y-2" data-testid="admin-tier-list">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">REWARD TIERS · {cfg.tiers.length}</p>
        {cfg.tiers.map((t, i) => (
          <TierRow key={i} index={i} tier={t} setTier={setTier} removeTier={removeTier} />
        ))}
      </div>

      <button type="button" onClick={addTier}
              data-testid="admin-tier-add-btn"
              className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost">
        <i className="fas fa-plus mr-1" />ADD TIER
      </button>
    </section>
  );
}

/** Single editable tier row — tier#, XP, free reward, PRO reward, milestone, remove. */
function TierRow({ index, tier, setTier, removeTier }) {
  return (
    <div data-testid={`admin-tier-row-${tier.tier}`}
         className={`grid grid-cols-1 md:grid-cols-[60px_100px_1fr_1fr_60px_30px] gap-2 items-center p-2 rounded-xl border ${
           tier.is_milestone ? "border-[#ffe066]/40 bg-[#ffe066]/5" : "border-white/10 bg-black/20"
         }`}>
      <input type="number" min="1" max="50"
             value={tier.tier}
             onChange={(e) => setTier(index, { tier: Math.max(1, parseInt(e.target.value) || 1) })}
             data-testid={`admin-tier-${tier.tier}-num`}
             className="bg-black/40 border border-white/15 rounded-lg px-2 py-1.5 text-white text-sm" />
      <input type="number" min="0" value={tier.xp_required}
             onChange={(e) => setTier(index, { xp_required: Math.max(0, parseInt(e.target.value) || 0) })}
             data-testid={`admin-tier-${tier.tier}-xp`}
             placeholder="XP"
             className="bg-black/40 border border-white/15 rounded-lg px-2 py-1.5 text-white text-sm font-mono" />
      <input type="text" value={tier.free_reward}
             onChange={(e) => setTier(index, { free_reward: e.target.value })}
             placeholder="🟢 FREE reward"
             data-testid={`admin-tier-${tier.tier}-free`}
             maxLength={120}
             className="bg-black/40 border border-white/15 rounded-lg px-2 py-1.5 text-white text-sm" />
      <input type="text" value={tier.pro_reward}
             onChange={(e) => setTier(index, { pro_reward: e.target.value })}
             placeholder="👑 PRO reward"
             data-testid={`admin-tier-${tier.tier}-pro`}
             maxLength={120}
             className="bg-black/40 border border-white/15 rounded-lg px-2 py-1.5 text-white text-sm" />
      <label className="flex items-center gap-1 text-[10px] text-white/70 cursor-pointer">
        <input type="checkbox" checked={!!tier.is_milestone}
               onChange={(e) => setTier(index, { is_milestone: e.target.checked })}
               data-testid={`admin-tier-${tier.tier}-milestone`} />
        MILE
      </label>
      <button type="button" onClick={() => removeTier(index)}
              data-testid={`admin-tier-${tier.tier}-remove`}
              className="text-[#14b8a6] hover:text-[#ff3a3a] text-sm">
        <i className="fas fa-xmark" />
      </button>
    </div>
  );
}
